import { cn } from '@/shared/lib';
import { useRTL } from '@/shared/hooks';
import { useTranslation } from 'react-i18next';
import { Typography } from '@/shared/components';
import { useActualiseOptimalDeliveryZone } from './hooks';
import { Dispatch, forwardRef, SetStateAction } from 'react';
import { BasketDeliveryPersonalInfo } from './BasketDeliveryPersonalInfo';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/shadcn';
import {
  clearErrors,
  setNameError,
  setPhoneError,
  useAppDispatch,
  useAppSelector,
  CustomerTabsEnum,
  setSelectedCustomerTab,
} from '@/app/store';

export interface BasketDeliveryProps {
  showSelector: boolean;
  setShowSelector: Dispatch<SetStateAction<boolean>>;
}

export const BasketDelivery = forwardRef<HTMLDivElement, BasketDeliveryProps>(({ ...props }, ref) => {
  const isRTL = useRTL();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('basket');
  const selectedCustomerTab = useAppSelector((state) => state.orderAnotherPerson.selectedCustomerTab);
  const isOrderForAnotherPersonEnabled = useAppSelector(
    (state) => state.initial.settings.isOrderForAnotherPersonEnabled,
  );

  useActualiseOptimalDeliveryZone(selectedCustomerTab === CustomerTabsEnum.Another);

  return (
    <div ref={ref} className='flex flex-col'>
      <Typography variant='h3' className={cn('mb-4', isRTL && 'text-right')}>
        {t('where.title')}
      </Typography>

      <Tabs
        value={selectedCustomerTab}
        dir={isRTL ? 'rtl' : 'ltr'}
        onValueChange={(v) => {
          dispatch(clearErrors(undefined));
          dispatch(setNameError(undefined));
          dispatch(setPhoneError(undefined));
          dispatch(setSelectedCustomerTab(v as CustomerTabsEnum));
        }}
        className='gap-0'
      >
        {isOrderForAnotherPersonEnabled && (
          <TabsList>
            <TabsTrigger value={CustomerTabsEnum.Myself}>{t('order.myself')}</TabsTrigger>

            <TabsTrigger value={CustomerTabsEnum.Another}>{t('order.another')}</TabsTrigger>
          </TabsList>
        )}

        <TabsContent value={CustomerTabsEnum.Myself} className={!isOrderForAnotherPersonEnabled ? 'mt-0' : ''}>
          <BasketDeliveryPersonalInfo {...props} />
        </TabsContent>

        <TabsContent value={CustomerTabsEnum.Another}>
          <BasketDeliveryPersonalInfo {...props} isOrderAnotherPerson />
        </TabsContent>
      </Tabs>
    </div>
  );
});
